<script setup lang="ts">
    import { computed, ref, watch, onMounted, onBeforeUnmount, onUpdated } from 'vue';
    import { Input } from '@/components/ui/input';
    import type { HTMLAttributes } from 'vue';
    import AutoNumeric from 'autonumeric';

    const props = defineProps<{
        modelValue?: number | string | null;
        placeholder?: string;
        class?: HTMLAttributes['class'];
        withDecimals?: boolean;
        hideZeroes?: boolean;
    }>();

    const emits = defineEmits<{
        (e: 'update:modelValue', payload: number | string | null): void;
    }>();

    // Template ref for the input element
    const inputRef = ref<InstanceType<typeof Input>>();
    let autoNumericInstance: AutoNumeric | null = null;

    // AutoNumeric configuration
    const autoNumericOptions = computed(() => ({
        digitGroupSeparator: '.',
        decimalCharacter: ',',
        decimalPlaces: props.withDecimals ? 2 : 0,
        decimalPlacesRawValue: props.withDecimals ? 2 : 0,
        allowDecimalPadding: true,
        emptyInputBehavior: 'null',
        selectOnFocus: false,
        // unformatOnSubmit: true,
        watchExternalChanges: true,
        modifyValueOnWheel: false,
    }));

    onMounted(() => {
        if (inputRef.value) {
            const domElement = inputRef.value.$el;

            if (domElement && domElement.tagName === 'INPUT') {
                try {
                    autoNumericInstance = new AutoNumeric(
                        domElement,
                        props.modelValue ?? '',
                        autoNumericOptions.value,
                    );

                    initializeValue(autoNumericInstance);

                    domElement.addEventListener('blur', handleInputChange);
                    domElement.addEventListener('keyup', handleInputChange);
                } catch (error) {
                    console.error('Failed to initialize AutoNumeric:', error);
                }
            }
        }
    });

    onUpdated(() => {
        if (autoNumericInstance) {
            const currentValue = autoNumericInstance.getNumber();

            if (props.modelValue !== currentValue) {
                try {
                    initializeValue(autoNumericInstance);
                } catch (error) {
                    console.error('Error setting AutoNumeric value:', error);
                }
            }
        }
    });

    const initializeValue = (autoNumericInstance: AutoNumeric) => {
        const isEmpty = [
            props.modelValue === undefined,
            props.modelValue === null,
            props.modelValue === '',
            ...(props.hideZeroes ? [props.modelValue === 0] : []),
        ].some(Boolean);

        if (isEmpty) {
            autoNumericInstance.clear();
        } else {
            autoNumericInstance.set(props.modelValue as string | number | null);
        }
    };

    onBeforeUnmount(() => {
        if (autoNumericInstance) {
            try {
                const domElement = inputRef.value?.$el;

                if (domElement && domElement.tagName === 'INPUT') {
                    domElement.removeEventListener('blur', handleInputChange);
                    domElement.removeEventListener('keyup', handleInputChange);
                }

                autoNumericInstance.remove();
            } catch (error) {
                console.error('Error during AutoNumeric cleanup:', error);
            } finally {
                autoNumericInstance = null;
            }
        }
    });

    const handleInputChange = () => {
        if (autoNumericInstance) {
            const domElement = inputRef.value?.$el;
            if (domElement && domElement.value === '') {
                emits('update:modelValue', null);
            } else {
                const rawValue = autoNumericInstance.getNumber();
                emits('update:modelValue', rawValue);
            }
        }
    };

    // Watch for changes to withDecimals prop and update configuration
    watch(
        () => props.withDecimals,
        () => {
            if (autoNumericInstance) {
                autoNumericInstance.update(autoNumericOptions.value);
            }
        },
    );
</script>

<template>
    <Input
        ref="inputRef"
        :placeholder="placeholder"
        :class="props.class"
        v-bind="$attrs"
    />
</template>
